import type { TryOutKind } from './registry';

const POLL_MS = 2500;
const MAX_POLLS = 720;

export interface PollContext {
  baseUrl: string;
  apiKey: string;
  signal: AbortSignal;
  onPoll: (body: unknown, lastCallAt: string) => void;
}

function terminalBoltz2(data: Record<string, unknown>): boolean {
  const s = data.status;
  if (typeof s !== 'string') return false;
  return s === 'COMPLETED' || s === 'FAILED';
}

function terminalBatch(data: Record<string, unknown>): boolean {
  const st = data.status;
  if (typeof st === 'string' && (st === 'COMPLETED' || st === 'FAILED')) {
    return true;
  }
  const summary = data.status_summary as Record<string, unknown> | undefined;
  if (!summary || typeof summary !== 'object') return false;
  const pending = summary.pending;
  const processing = summary.processing;
  if (typeof pending !== 'number' || typeof processing !== 'number') return false;
  return pending === 0 && processing === 0;
}

export async function pollUntilDone(
  kind: TryOutKind,
  requestId: string,
  ctx: PollContext
): Promise<{ lastJson: unknown; lastCallAt: string }> {
  const path =
    kind === 'boltz-3d-affinity'
      ? `${ctx.baseUrl.replace(/\/$/, '')}/requests/${encodeURIComponent(requestId)}`
      : `${ctx.baseUrl.replace(/\/$/, '')}/requests/${encodeURIComponent(requestId)}`;

  let lastJson: unknown = null;
  let lastCallAt = new Date().toISOString();

  for (let i = 0; i < MAX_POLLS; i++) {
    if (ctx.signal.aborted) throw new DOMException('Aborted', 'AbortError');

    await new Promise((r) => setTimeout(r, i === 0 ? 0 : POLL_MS));
    if (ctx.signal.aborted) throw new DOMException('Aborted', 'AbortError');

    const res = await fetch(path, {
      method: 'GET',
      headers: { 'x-api-key': ctx.apiKey },
      signal: ctx.signal,
    });
    lastCallAt = new Date().toISOString();
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = { _parseError: true, raw: text };
    }
    lastJson = parsed;
    ctx.onPoll(parsed, lastCallAt);

    if (!res.ok) {
      return { lastJson, lastCallAt };
    }

    const data = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    if (!data) continue;

    const done = kind === 'boltz-3d-affinity' ? terminalBoltz2(data) : terminalBatch(data);
    if (done) {
      return { lastJson, lastCallAt };
    }
  }

  throw new Error('Polling timed out before the job finished.');
}
